import React, { useState } from "react"
import { Alert, Button, Checkbox, Form, FormInstance, Input, Select, Space, StepProps, Steps, Tooltip, Typography } from "antd"
import { AntdFormItem } from "../AntdFormItem/AntdFormItem"
import { RegisterStudentCourse } from "./types"
import { relationToStudent, schoolRegions, yearOfStudies } from "./enums"
import { css } from "@emotion/react"
import Link from "next/link"
import { QuestionCircleOutlined } from "@ant-design/icons"
import { RegisterCourseCustomerFormFields } from "./RegisterCourseCustomerFormFields"
import { RegisterCourseEquipmentFormItems } from "./RegisterCourseEquipmentFormItems"
import { RegisterCourseCouponField } from "./RegisterCourseCouponField"
import { ReferralSourceFormItem } from "./ReferralSourceFormItem"
import { WithGiftVoucherFormItem } from "./WithGiftVoucherFormItem"
import { addSubscriberByEmail } from "../../lib/ecomail/addSubscriberByEmail"
import { RegisterThankYouModalWindow } from "./RegisterThankYouModalWindow"
import { getWebSiteUrlFromWindow } from "../../lib/url/getWebSiteUrlFromWindow"
import { FormikErrors } from "formik"
import { get, isEmpty } from "lodash"
import { FormikTouched } from "formik/dist/types"
import { MappedCourse } from "../../lib/prismic/MappedCourse"
import { CourseLecturer, CourseTechnology, getTechnology } from "../../lib/prismic/mapData"
import { OutputCourseRegistration } from "../../lib/supabase/insertCourseRegistration"
import { CheckCourseCapacity } from "../../lib/supabase/checkCourseCapacity"
import { getEmailRecipients } from "../../lib/supabase/registrations/getEmailRecipients"

const commonCol = {
  labelCol: {
    span: 24 - 10
  },
  wrapperCol: {
    span: 10
  }
}

const capitalize = (s: string) => s && String(s[0]).toUpperCase() + String(s).slice(1)

export const RegistrationFormWithStepsInnerForm: React.FC<Props> = ({
  form,
  values,
  handleSubmit,
  handleChange,
  setFieldValue,
  handleBlur,
  touched,
  errors,
  isSubmitting,
  validateField,
  course,
  lecturer,
  formCompleteSend,
  courseWithEquipment,
  capacityStatus,
  courseTechnology
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const hasSubscribedRef = React.useRef(false)

  const next = () => {
    setCurrentStep(currentStep + 1)
  }

  const prev = () => {
    setCurrentStep(currentStep - 1)
  }

  const onChange = (currentStep: number) => {
    setCurrentStep(currentStep)
  }

  const getErrorIcon = (
    errors: FormikErrors<RegisterStudentCourse>,
    touched: FormikTouched<RegisterStudentCourse>,
    stepFields: string[]
  ): "wait" | "process" | "finish" | "error" | undefined => {
    return stepFields.some((field) => !isEmpty(get(errors, field)) && !isEmpty(get(touched, field))) ? "error" : undefined
  }

  const stepItems: Array<StepProps & { content: React.ReactNode }> = [
    {
      title: "Kontaktní údaje rodiče",
      //description: "Zadejte jméno, e-mail a telefon pro zajištění komunikace.",
      //icon:
      //subTitle: "(zákonný zástupce)",
      status: getErrorIcon(errors, touched, [
        "studentContact.email",
        "studentContact.firstname",
        "studentContact.lastname",
        "studentContact.phone",
        "studentContact.relationToStudent",
        "consent"
      ]),
      content: (
        <>
          <AntdFormItem<RegisterStudentCourse> name="studentContact.email" label="Email" errors={errors} touched={touched} {...commonCol}>
            <Input value={values.studentContact?.email} onChange={handleChange} onBlur={handleBlur} />
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse> name="studentContact.firstname" label="Jméno" errors={errors} touched={touched} {...commonCol}>
            <Input value={values.studentContact?.firstname} onChange={handleChange} onBlur={handleBlur} />
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse> name="studentContact.lastname" label="Přijmení" errors={errors} touched={touched} {...commonCol}>
            <Input value={values.studentContact?.lastname} onChange={handleChange} onBlur={handleBlur} />
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse> name="studentContact.phone" label="Telefon" errors={errors} touched={touched} {...commonCol}>
            <Input value={values.studentContact?.phone} onChange={handleChange} onBlur={handleBlur} />
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse>
            name="studentContact.relationToStudent"
            label="Vztah kontaktní osoby k žákovi"
            errors={errors}
            touched={touched}
            {...commonCol}>
            <Select
              value={values.studentContact?.relationToStudent}
              onChange={(value) => setFieldValue("studentContact.relationToStudent", value, true)}
              onBlur={handleBlur}>
              {relationToStudent.map((s) => (
                <Select.Option key={s} value={s}>
                  {capitalize(s)}
                </Select.Option>
              ))}
            </Select>
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse>
            name="consent"
            label=""
            errors={errors}
            touched={touched}
            css={css`
              .ant-row {
                flex-direction: row-reverse;
              }
            `}>
            <Checkbox checked={values.consent} onChange={handleChange}>
              Souhlasím se zpracováním osobních údajů za účelem dokončení registrace a zasílání informačních e-mailů v souladu se{" "}
              <Link href={"/zpracovani-osobnich-udaju"} title="Zpracování osobních údajů" target="_blank">
                zpracováním osobních údajů
              </Link>
              .
            </Checkbox>
          </AntdFormItem>
        </>
      )
    },
    {
      title: "Informace o žákovi",
      status: getErrorIcon(errors, touched, [
        "student.email",
        "student.firstname",
        "student.lastname",
        "student.schoolRegion",
        "student.school",
        "student.yearOfStudies"
      ]),
      content: (
        <>
          <AntdFormItem<RegisterStudentCourse> name="student.email" label="Email" errors={errors} touched={touched} {...commonCol}>
            <Input value={values.student.email} onChange={handleChange} onBlur={handleBlur} />
          </AntdFormItem>

          {values.student.email.length > 0 && values.studentContact.email.length > 0 && values.student.email === values.studentContact.email && (
            <Typography.Text type="warning" style={{ top: -10, position: "relative" }}>
              Vypadá to, že jste zadali stejný e-mail pro rodiče i žáka. Pro účely komunikace a zasílání materiálů je potřeba zadat rozdílné e-mailové adresy.
              Prosím, ujistěte se, že e-mail žáka je jiný než e-mail rodiče.
            </Typography.Text>
          )}

          <AntdFormItem<RegisterStudentCourse> name="student.firstname" label="Jméno" errors={errors} touched={touched} {...commonCol}>
            <Input value={values.student.firstname} onChange={handleChange} onBlur={handleBlur} />
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse> name="student.lastname" label="Přijmení" errors={errors} touched={touched} {...commonCol}>
            <Input value={values.student.lastname} onChange={handleChange} onBlur={handleBlur} />
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse>
            name="student.schoolRegion"
            label={
              <Space size={4}>
                <span>Kraj školy</span>
                <Tooltip title="V jakém kraji se nachází škola žáka.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            }
            errors={errors}
            touched={touched}
            {...commonCol}>
            <Select value={values.student.schoolRegion} onChange={(value) => setFieldValue("student.schoolRegion", value, true)} onBlur={handleBlur}>
              {schoolRegions.map((s) => (
                <Select.Option key={s} value={s}>
                  {s}
                </Select.Option>
              ))}
            </Select>
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse> name="student.school" label="Škola" errors={errors} touched={touched} {...commonCol}>
            <Input value={values.student.school} onChange={handleChange} onBlur={handleBlur} />
          </AntdFormItem>

          <AntdFormItem<RegisterStudentCourse>
            name="student.yearOfStudies"
            label={
              <Space size={4}>
                <span>Ročník</span>
                <Tooltip title="U gymnazistů zaškrtněte běžný ekvivalent.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            }
            errors={errors}
            touched={touched}
            {...commonCol}>
            <Select value={values.student.yearOfStudies} onChange={(value) => setFieldValue("student.yearOfStudies", value, true)} onBlur={handleBlur}>
              {yearOfStudies.map((s) => (
                <Select.Option key={s} value={s}>
                  {s}
                </Select.Option>
              ))}
            </Select>
          </AntdFormItem>
        </>
      )
    },
    {
      title: "Dokončení registrace",
      status: getErrorIcon(errors, touched, [
        "equipmentBuyFromOrganization",
        "coupon",
        "referralSource",
        "iSignUp",
        "newsletterAgreement",
        "withGiftVoucher",
        "invoice"
      ]),
      content: (
        <>
          <RegisterCourseCustomerFormFields
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
            coursePrice={course.price}
          />
          {course.price > 0 && <Typography.Title level={3}>Ostatní</Typography.Title>}
          <RegisterCourseEquipmentFormItems
            courseWithEquipment={courseWithEquipment}
            values={values}
            course={course}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
          />
          <RegisterCourseCouponField coupon={values.coupon} touched={touched} errors={errors} handleBlur={handleBlur} handleChange={handleChange} />
          <ReferralSourceFormItem handleBlur={handleBlur} errors={errors} touched={touched} setFieldValue={setFieldValue} values={values} course={course} />
          <AntdFormItem<RegisterStudentCourse>
            name="iSignUp"
            label=""
            errors={errors}
            touched={touched}
            css={css`
              .ant-row {
                flex-direction: row-reverse;
              }
            `}>
            <Checkbox checked={values.iSignUp} onChange={handleChange}>
              Závazně přihlašuji osobu uvedenou jako &quot;žák&quot; na kurz &quot;{course.name}&quot;.
            </Checkbox>
          </AntdFormItem>
          <AntdFormItem<RegisterStudentCourse>
            name="newsletterAgreement"
            label=""
            errors={errors}
            touched={touched}
            css={css`
              .ant-row {
                flex-direction: row-reverse;
              }
            `}>
            <Checkbox checked={values.newsletterAgreement} onChange={handleChange}>
              Chci dostávat upozornění e-mailem o všech akcích organizace Vzdělání budoucnosti, z.s.
            </Checkbox>
          </AntdFormItem>

          <WithGiftVoucherFormItem touched={touched} errors={errors} values={values} onChange={handleChange} />

          <Space direction="vertical">
            {Object.keys(errors).length > 0 && <Alert type="error" message={`Některé pole by mělo být vyplněno.`} banner />}

            {capacityStatus?.all.isFull && (
              <Alert type="warning" message="Kurz je naplněn. I tak stále přijímáme přihlášky. Budeme Vás informovat. Děkujeme" banner />
            )}

            <Space direction="vertical" align="end">
              <div>
                <Typography.Text>
                  Potvrzení o registraci společně s platebními údaji Vám zašleme na uvedený kontaktní e-mail ihned po úspěšném odeslání přihlášky.
                </Typography.Text>
              </div>

              <Space>
                {formCompleteSend && <Alert type="success" message="Registrace proběhla úspěšně. Očekávejte potvrzení ve vašem emailu. Děkujeme!" />}
              </Space>
            </Space>
          </Space>
        </>
      )
    }
  ]

  return (
    <>
      <Form form={form}>
        <Steps items={stepItems} current={currentStep} direction="horizontal" onChange={onChange} />

        <div
          className="steps-content"
          css={css`
            margin: 30px 0 30px 0;
          `}>
          {stepItems[currentStep].content}
        </div>

        <div
          className="steps-action"
          css={css`
            display: flex;
            width: 100%;
            justify-content: space-between;
          `}>
          <div
            css={css`
              align-self: flex-start;
            `}>
            {currentStep > 0 && <Button onClick={() => prev()}>Zpět</Button>}
          </div>

          <div
            css={css`
              align-self: flex-end;
            `}>
            {currentStep < stepItems.length - 1 && (
              <Space direction="vertical" align="end">
                <Button
                  type="primary"
                  onClick={async () => {
                    const step0Fields = [
                      "studentContact.email",
                      "studentContact.firstname",
                      "studentContact.lastname",
                      "studentContact.phone",
                      "studentContact.relationToStudent",
                      "consent"
                    ]

                    const requiredFields = ["studentContact.email", "consent"]

                    const isAnyOtherFieldFilled = step0Fields.some((field) => !requiredFields.includes(field) && values[field]?.trim())

                    const fieldsToValidate = isAnyOtherFieldFilled ? step0Fields : requiredFields

                    fieldsToValidate.forEach(validateField)

                    const emails = getEmailRecipients(values.withGiftVoucher, values.studentContact.email, values.student.email)
                    if (values.consent && !hasSubscribedRef.current) {
                      try {
                        await Promise.all(
                          emails.map(
                            async (emailInLoop) =>
                              await addSubscriberByEmail(
                                emailInLoop,
                                ["krouzky", "rozpracovana registrace", "legal representative", courseTechnology.slug],
                                course.slug,
                                true,
                                true,
                                7
                              )
                          )
                        )

                        hasSubscribedRef.current = true
                      } catch (err) {
                        console.error(err)
                      }
                    }

                    next()
                  }}>
                  Pokračovat v registraci
                </Button>
              </Space>
            )}

            {currentStep === stepItems.length - 1 && (
              <Button type="primary" htmlType="submit" onClick={() => handleSubmit()} loading={isSubmitting}>
                Odeslat registraci
              </Button>
            )}
          </div>
        </div>
      </Form>

      {formCompleteSend && <RegisterThankYouModalWindow course={course} lecturer={lecturer} values={values} register={formCompleteSend} />}
    </>
  )
}

interface Props {
  form: FormInstance<any>
  course: MappedCourse
  lecturer: CourseLecturer
  values: RegisterStudentCourse
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
  handleChange: (e: any) => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<RegisterStudentCourse>>
  handleBlur: (e: React.FocusEvent<any, Element>) => void
  touched: FormikTouched<RegisterStudentCourse>
  errors: FormikErrors<RegisterStudentCourse>
  isSubmitting: boolean
  validateField: (field: string) => void
  courseWithEquipment: boolean
  formCompleteSend: OutputCourseRegistration
  capacityStatus: CheckCourseCapacity
  courseTechnology: CourseTechnology
}
